/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import {
  AbstractFormComponent,
  ContactDetailsDialogConfig,
  contactDetailsDialogRoute,
  FormFlowState,
} from '@gh/builder-shared';
import { BuilderPermission, ContactType } from '@gh/builder-shared-data';
import { TimerEvent } from '@gh/common-internal-shared';
import {
  ActionFlow,
  ContainerState,
  DEFAULT_AUTOSAVE_INTERVALS,
  getRouteInitData,
  getRouteParentTitle,
  ImmutablePageState,
  PAGE_STATE,
  queryDirty,
  queryRecalculationNeeded,
  State,
  UserContext,
} from '@gh/common-shared';
import { getLocalValue, setLocalValue } from '@gh/core-util';
import { filter$, map$, skip$ } from '@gh/rx/operators';
import { Observable, Subscription } from 'rxjs';
import { isJeComponentCostCenter } from '../../resources/job-estimate-details.model';
import { JeCommandService } from './je-command.service';
import {
  JE_DETAILS_INIT_STATE,
  JeDetailsMasterInfo,
  JeDetailsState,
  queryJeAuthorized,
  queryJeCanChangeAuthorization,
  queryJeEditable,
  queryJeFormMode,
  queryJeMasterInfo,
  queryJeSelectedCostCenter,
  queryJeUnconfirmedVarCount,
} from './je-details.state';
import { jeDetailsReducer } from './reducers/je-details.reducers';
import { DialogService } from '@gh/core-ui';
import { TranslationTupleOrId } from '@gh/core-mls';

const JE_AUTOSAVE_LOCAL_KEY = 'je:autosave';

export function jeDetailsStateFactory(): State<JeDetailsState> {
  return new State(JE_DETAILS_INIT_STATE, jeDetailsReducer);
}

export function jeDetailsFormFactory(state: State<JeDetailsState>): ImmutablePageState<JeDetailsState> {
  return new ImmutablePageState(state, {
    dirty: queryDirty,
    formMode: queryJeFormMode,
  });
}

@Component({
  standalone: false,
  selector: 'gh-job-estimate-details',
  templateUrl: 'job-estimate-details.component.html',
  styleUrls: ['job-estimate-details.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    { provide: State, useFactory: jeDetailsStateFactory },
    { provide: PAGE_STATE, useFactory: jeDetailsFormFactory, deps: [State] },
    JeCommandService,
  ],
})
export class JobEstimateDetailsComponent extends AbstractFormComponent<JeDetailsState> implements OnInit, OnDestroy {
  cost$: Observable<number>;
  contractAmount$: Observable<number>;
  variationsAmount$: Observable<number>;
  currentPrice$: Observable<number>;
  recalculationNeeded$: Observable<boolean>;
  dirty$: Observable<boolean>;
  isComponentCostCenter$: Observable<boolean>;
  isTypicalCostCenter$: Observable<boolean>;
  variationCount$: Observable<number>;
  editable$: Observable<boolean>;
  authorized$: Observable<boolean>;
  canChangeAuthorization$: Observable<boolean>;
  autosaveIntervals = DEFAULT_AUTOSAVE_INTERVALS;
  currentAutosaveInterval = getLocalValue(JE_AUTOSAVE_LOCAL_KEY, '30m');
  autoSaveActivityTick = 0;
  isUndoDisabled$: Observable<boolean>;
  isRedoDisabled$: Observable<boolean>;
  private autoSaveActivitySubscription?: Subscription;

  reportUrl: string;

  constructor(private route: ActivatedRoute,
              private state: State<JeDetailsState>,
              private commands: JeCommandService,
              private userContext: UserContext,
              private dialogService: DialogService,
              private changeDetectorRef: ChangeDetectorRef,
              @Inject(PAGE_STATE) public form: ContainerState<JeDetailsState>) {
    super();
  }

  get masterInfo(): JeDetailsMasterInfo {
    return this.state.project(queryJeMasterInfo);
  }

  get completeFlow(): ActionFlow<FormFlowState<JeDetailsState>> {
    return this.commands.completeFlow();
  }

  get parentTitle(): TranslationTupleOrId {
    return getRouteParentTitle(this.route) || 'lbl_jobs';
  }

  ngOnInit(): void {
    const { details } = getRouteInitData(this.route);
    const { jeItemGridLayout, jeComponentGridLayout, reportUrl } = getRouteInitData(this.route);

    this.commands.load(
      details,
      { jeItems: jeItemGridLayout, jeComponents: jeComponentGridLayout },
      this.userContext.hasPermission(BuilderPermission.Estimating.JobEstimate.EDIT));

    this.reportUrl = reportUrl;

    this.cost$ = this.state.select(queryJeMasterInfo).pipe(map$((master) => master.cost));
    this.contractAmount$ = this.state.select(queryJeMasterInfo).pipe(map$((master) => master.contractAmount));
    this.variationsAmount$ = this.state.select(queryJeMasterInfo).pipe(map$((master) => master.variationsAmount));
    this.currentPrice$ = this.state.select(queryJeMasterInfo).pipe(
      map$(({ contractAmount, variationsAmount }) => contractAmount + variationsAmount));
    this.variationCount$ = this.state.select(queryJeUnconfirmedVarCount);
    this.recalculationNeeded$ = this.state.select(queryRecalculationNeeded);
    this.dirty$ = this.state.select(queryDirty);

    this.editable$ = this.state.select(queryJeEditable);
    this.authorized$ = this.state.select(queryJeAuthorized);
    this.canChangeAuthorization$ = this.state.select(queryJeCanChangeAuthorization);

    const costCenter$ = this.state.select(queryJeSelectedCostCenter);
    this.isComponentCostCenter$ = costCenter$.pipe(map$((cc) => cc && isJeComponentCostCenter(cc) || false));
    this.isTypicalCostCenter$ = costCenter$.pipe(map$((cc) => cc && !isJeComponentCostCenter(cc) || false));
    this.setHistoryObservable();
    this.autoSaveActivitySubscription = this.state.pipe(skip$(1)).subscribe(() => {
      if (this.state.project(queryDirty)) {
        this.autoSaveActivityTick++;
        this.changeDetectorRef.markForCheck();
      }
    });
  }

  ngOnDestroy(): void {
    this.autoSaveActivitySubscription?.unsubscribe();
  }

  setHistoryObservable(): void {
    this.commands.initHistory();
    this.isUndoDisabled$ = this.commands.isUndoDisabled$;
    this.isRedoDisabled$ = this.commands.isRedoDisabled$;
  }

  undo(): void {
    this.commands.undo();
  }

  redo(): void {
    this.commands.redo();
  }

  onChangeAutosaveInterval(interval: string): void {
    setLocalValue(JE_AUTOSAVE_LOCAL_KEY, interval);
  }

  onAutoSave(event: TimerEvent): void {
    event.waitFor(this.commands.autoSave());
  }

  onRecalculate(): void {
    this.commands.recalculate();
  }

  onOpenBom(): void {
    this.commands.openReport(this.reportUrl);
  }

  onOpenOrders(): void {
    this.commands.openOrders();
  }

  onReprice(): void {
    this.commands.reprice();
  }

  onLockAll(): void {
    this.commands.lockAll();
  }

  onUnlockAll(): void {
    this.commands.unlockAll();
  }

  onAuthorize(): void {
    this.commands.authorize();
  }

  onUnauthorize(): void {
    this.commands.unauthorize();
  }

  onNextUnconfirmedVariation(): void {
    this.commands.navigateToNextUnconfirmedVariation();
  }

  openContact(): void {
    this.dialogService.openWithInput(contactDetailsDialogRoute, <ContactDetailsDialogConfig>{
      contactType: ContactType.Customer,
      contactId: this.masterInfo.contactId,
      parentTitle: 'lbl_job',
    }).afterClosed().pipe(
      filter$(Boolean))
      .subscribe((contact: any) => {
        this.masterInfo.contactId = contact.contactId;
        this.masterInfo.customerName = contact.name;
      });
  }
}
